import {
  API,
  GRAFANA_SESSION,
  HEADLAMP_SESSION,
  MINIO_SESSION,
  PROJECTS_SESSION,
} from "@/axios-config";

// Headlamp/Grafana stay hosted in the cluster (see manifests/apm62/README.md
// for why), gated by the cluster's own Traefik forwardAuth checking a
// session against the cluster's own backend/DB. This same frontend build now
// runs from two different origins (apm62 during the migration, and the
// cluster itself) — only one of them can actually mint a session the
// cluster's forwardAuth will recognize:
//   - Already on the cluster's own origin: same-origin credentialed POST
//     works fine, mint first, then navigate (this used to be the only
//     branch that existed, before apm62 became a second origin serving the
//     same build - see git history for the original mint+navigate version).
//   - On apm62 (a different origin): a session minted against apm62's own
//     separate backend/DB wouldn't be recognized by the cluster's
//     forwardAuth anyway, and a cross-origin credentialed POST would need
//     CORS changes on the live cluster backend for this alone. Just
//     navigate and let the cluster's own sign-in handle it independently.
// Getting this branch wrong is not cosmetic: navigating to /headlamp/ or
// /monitor/ from the cluster's own origin *without* minting first causes a
// real infinite redirect loop - forwardAuth rejects, bounces to
// /accounts/signin?next=..., the SPA reloads already-logged-in, retries
// the same navigation immediately, rejects again, forever.
export const CLUSTER_BASE_URL = "https://camceadhub.ast.cam.ac.uk";

function isOnClusterOrigin() {
  return window.location.origin === CLUSTER_BASE_URL;
}

export function goToHeadlamp() {
  if (isOnClusterOrigin()) {
    return API.post(HEADLAMP_SESSION).then(() => {
      window.location.href = `${CLUSTER_BASE_URL}/headlamp/`;
    });
  }
  window.location.href = `${CLUSTER_BASE_URL}/headlamp/`;
  return Promise.resolve();
}

// Imperial/STFC Headlamp - added 2026-09-10, same reasoning as
// goToGrafanaImperial/Stfc below (see that comment) - reuses the identical
// HeadlampSessionView/HeadlampSessionCheckView grant as ARCUS's own
// goToHeadlamp above, just a different destination path under APM62_BASE_URL
// rather than CLUSTER_BASE_URL (no second-origin case to branch on, these
// routes only ever exist on apm62's own nginx).
export function goToHeadlampImperial() {
  return API.post(HEADLAMP_SESSION).then(() => {
    window.location.href = `${APM62_BASE_URL}/headlamp-imperial/`;
  });
}

export function goToHeadlampStfc() {
  return API.post(HEADLAMP_SESSION).then(() => {
    window.location.href = `${APM62_BASE_URL}/headlamp-stfc/`;
  });
}

export function goToGrafana() {
  if (isOnClusterOrigin()) {
    return API.post(GRAFANA_SESSION).then(() => {
      window.location.href = `${CLUSTER_BASE_URL}/monitor/`;
    });
  }
  window.location.href = `${CLUSTER_BASE_URL}/monitor/`;
  return Promise.resolve();
}

// Imperial/STFC Grafana + all 3 sites' Prometheus UI - added 2026-09-10, same
// CAMCEADHUB-superuser-only gate as ARCUS's own goToGrafana() above (all 5
// routes are backed by the identical GrafanaSessionCheckView/session grant -
// see CAMCEADHUB_SYSTEM_README.md §B.42.7 for why one check covers all of
// these, including Prometheus's own UI, which has no separate concept of its
// own). Network path is different (a local-only kubectl tunnel per site
// rather than the cluster's own Traefik, since neither site has a
// LoadBalancer-capable ingress - same doc section) but that's entirely
// behind apm62's nginx; from the frontend's own perspective this is just a
// different path under the same APM62_BASE_URL, always minted the same way
// regardless of origin (unlike Headlamp/Grafana above, these routes only
// ever exist on apm62's own nginx, never the cluster's Traefik, so there's
// no second-origin case to branch on here).
export function goToGrafanaImperial() {
  return API.post(GRAFANA_SESSION).then(() => {
    window.location.href = `${APM62_BASE_URL}/monitor-imperial/`;
  });
}

export function goToGrafanaStfc() {
  return API.post(GRAFANA_SESSION).then(() => {
    window.location.href = `${APM62_BASE_URL}/monitor-stfc/`;
  });
}

// CASPER's own dashboard (implementation_plan.md Phase 8, added 2026-09-22) - same
// STFC Grafana instance/session grant as goToGrafanaStfc above (there's only one
// Grafana per site, not a separate one per project), just deep-linked straight to the
// dashboard by its uid ("casper-overview", set in
// observability/casper-grafana-dashboard.json) rather than landing on Grafana's home
// screen and making an admin hunt for it.
export function goToGrafanaCasper() {
  return API.post(GRAFANA_SESSION).then(() => {
    window.location.href = `${APM62_BASE_URL}/monitor-stfc/d/casper-overview/`;
  });
}

export function goToPrometheusArcus() {
  return API.post(GRAFANA_SESSION).then(() => {
    window.location.href = `${APM62_BASE_URL}/prometheus-arcus/`;
  });
}

export function goToPrometheusImperial() {
  return API.post(GRAFANA_SESSION).then(() => {
    window.location.href = `${APM62_BASE_URL}/prometheus-imperial/`;
  });
}

export function goToPrometheusStfc() {
  return API.post(GRAFANA_SESSION).then(() => {
    window.location.href = `${APM62_BASE_URL}/prometheus-stfc/`;
  });
}

// Single source of truth for "an already-authenticated visitor landed on
// /accounts/signin?next=<admin tool path>, which one do we mint a session for and
// navigate to" - added 2026-09-22 after PublicPage.vue's mounted() and SignIn.vue's
// userValidateOTP() were found to each hardcode their own `next === "/headlamp/" ||
// next === "/monitor/"` check, both stale since the 2026-09-10 multi-site rollout added
// 7 more nginx routes (/monitor-imperial/, /monitor-stfc/, /prometheus-arcus/,
// /prometheus-imperial/, /prometheus-stfc/, /headlamp-imperial/, /headlamp-stfc/) that
// neither check list ever learned about - any of those 7 silently fell through to a
// bare Profile redirect instead of reaching Grafana/Headlamp/Prometheus. Keyed by the
// exact nginx `next` path (see sites-available/camceadhub) so adding a future site is
// one new entry here, not a third hardcoded list to remember to update.
export const ADMIN_TOOL_REDIRECTS = {
  "/headlamp/": goToHeadlamp,
  "/headlamp-imperial/": goToHeadlampImperial,
  "/headlamp-stfc/": goToHeadlampStfc,
  "/monitor/": goToGrafana,
  "/monitor-imperial/": goToGrafanaImperial,
  "/monitor-stfc/": goToGrafanaStfc,
  "/monitor-stfc/d/casper-overview/": goToGrafanaCasper,
  "/prometheus-arcus/": goToPrometheusArcus,
  "/prometheus-imperial/": goToPrometheusImperial,
  "/prometheus-stfc/": goToPrometheusStfc,
};

// One-shot loop guard - added 2026-09-22 after a real, reproduced rapid-flicker bug:
// 7 of these 9 destinations sit behind an IP allowlist (Cambridge network/VPN ranges
// only, added 2026-09-10 - see each nginx location's own "Network-layer restriction"
// comment) that runs BEFORE the auth_request session check, so a browser outside that
// range gets denied and 302'd straight back to /accounts/signin?next=<same path> no
// matter how valid its session is. Neither PublicPage.vue's mounted() nor SignIn.vue's
// userValidateOTP() had any way to tell "first arrival here" apart from "we just got
// bounced straight back" - every fresh SPA boot on that URL retried the mint+navigate
// unconditionally, and since the session mint itself always succeeds (it hits a
// completely unrestricted /api/ location, only the destination is IP-gated), the retry
// never stopped on its own: mint succeeds, navigate, nginx denies, redirect, SPA
// reboots, repeat - a genuine loop, fast because every hop is two local round trips,
// not one slow one.
//
// sessionStorage (not localStorage - this is a one-shot per browser tab/session, not a
// standing preference) survives the full-page reload each loop iteration does but
// resets on a fresh tab, so a later, different problem gets its own fresh attempt
// rather than being permanently silenced by a stale flag from today.
//
// Returns true if it handled the redirect attempt (whether that means "navigating now"
// or "already retried once, gave up") - false only when `next` isn't a recognized admin
// tool path at all, so the caller knows to fall through to its own default behaviour.
export function attemptAdminToolRedirect(next, onGiveUp) {
  const goTo = ADMIN_TOOL_REDIRECTS[next];
  if (!goTo) {
    return false;
  }
  const guardKey = `admin_redirect_attempted:${next}`;
  if (sessionStorage.getItem(guardKey)) {
    // Second time landing here in this tab with nothing in between - the first
    // attempt's navigation bounced straight back rather than reaching its target.
    // Stop retrying and let the caller show/do something terminal instead.
    sessionStorage.removeItem(guardKey);
    onGiveUp(
      new Error(
        `admin tool redirect loop detected for ${next} - likely denied by the destination's own IP allowlist (Cambridge network/VPN only)`
      )
    );
    return true;
  }
  sessionStorage.setItem(guardKey, "1");
  // Deliberately NOT cleared in a .then() on success - goTo()'s own success path sets
  // window.location.href and resolves; JS keeps running until the browser actually
  // unloads the page, so a .then()-based cleanup here would fire and clear the guard
  // BEFORE the real navigation completes, defeating it entirely (the browser would land
  // back on this exact signin URL, if denied, with no guard left to catch the retry).
  // Leaving it set is safe: it only matters if the browser returns to this exact
  // next= value, and a real successful navigation away means it never does.
  goTo().catch((error) => {
    sessionStorage.removeItem(guardKey);
    onGiveUp(error);
  });
  return true;
}

// camcead-astro runs on apm62 itself (systemd service, 127.0.0.1:8765, not
// reachable from inside the k8s cluster at all) - gated by nginx's
// auth_request behind CAMCEADHUB's own admin login (password+2FA) since
// 2026-07-28, same shape as Headlamp/Grafana's session gate above (its own
// standalone login, camcead_astro/web/auth.py, is disabled in favor of this
// via CAMCEAD_ASTRO_REQUIRE_OWN_LOGIN=false, so this needs to actually mint
// a session first, not just navigate).
//
// DNS cutover happened 2026-07-28: camceadhub.ast.cam.ac.uk now points directly
// at apm62, which is the real production deployment - not the k8s-cluster copy.
// This constant used to be the raw apm62.ast.cam.ac.uk:8091 test address,
// always absolute so it also worked from the (still then-canonical)
// cluster-hosted copy, whose own Traefik has no /projects/ route. Now that
// apm62 *is* canonical, this is just the real hostname - same value as
// CLUSTER_BASE_URL below, kept as a separate constant since the two names
// mean different things (this one is "wherever camcead-astro actually runs",
// that one is "wherever the cluster's own Traefik is") even though they
// currently happen to share a value.
export const APM62_BASE_URL = "https://camceadhub.ast.cam.ac.uk";

export function goToProjects() {
  return API.post(PROJECTS_SESSION).then(() => {
    window.location.href = `${APM62_BASE_URL}/projects/`;
  });
}

// MinIO console, gated the same way as Projects above (2026-07-28) - on its own port
// rather than a path under APM62_BASE_URL, since the browser needs to actually reach
// camceadhub.ast.cam.ac.uk:8095 for the CAMCEADHUB session cookie to be sent at all
// (cookies are scoped by hostname, not port, but a *different* hostname like the old
// apm62.ast.cam.ac.uk:8093 address would never see it). See nginx-camceadhub.conf's
// :8095 server block and MinioSessionView's docstring for the rest of the reasoning.
// MinIO still shows its own login form after this - this only gates who can reach the
// console at all.
export const MINIO_BASE_URL = "https://camceadhub.ast.cam.ac.uk:8095";

export function goToMinio() {
  return API.post(MINIO_SESSION).then(() => {
    window.location.href = `${MINIO_BASE_URL}/`;
  });
}
